import { ChangeDetectionStrategy, Component, computed, input, signal, inject } from '@angular/core';
import {
  OverlayModule,
  ConnectionPositionPair,
  ConnectedOverlayPositionChange,
  Overlay,
} from '@angular/cdk/overlay';

type HintSegment = { kind: 'text'; text: string } | { kind: 'link'; text: string; href: string };

type HintBlock =
  | { kind: 'paragraph'; segments: HintSegment[] }
  | { kind: 'list'; items: HintSegment[][] };

@Component({
  selector: 'app-hint',
  imports: [OverlayModule],
  templateUrl: './hint.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inline-flex items-end',
  },
})
export class HintComponent {
  private readonly overlay = inject(Overlay);
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly scrollStrategy = this.overlay.scrollStrategies.close();

  /** The text to display in the tooltip. */
  readonly hint = input.required<string>();

  /** The label of the field this hint is associated with (for accessibility). */
  readonly label = input<string>('');

  /** Unique identifier for the hint tooltip. */
  readonly id = input<string>();

  /** Internal state for hint placement ('top' or 'bottom'). */
  readonly placement = signal<'top' | 'bottom'>('top');

  /** Internal state for tooltip visibility. */
  readonly isOpen = signal(false);

  readonly hintBlocks = computed<HintBlock[]>(() => this.parseHint(this.hint()));
  readonly tooltipWidth = computed(() => {
    const hintLength = this.hint().trim().length;

    if (hintLength > 550) {
      return '34rem';
    }

    if (hintLength > 320) {
      return '30rem';
    }

    if (hintLength > 180) {
      return '26rem';
    }

    return null;
  });

  /** Overlay positions */
  readonly positions = [
    new ConnectionPositionPair(
      { originX: 'center', originY: 'top' },
      { overlayX: 'center', overlayY: 'bottom' },
      0,
      -8, // offset Y
    ),
    new ConnectionPositionPair(
      { originX: 'center', originY: 'bottom' },
      { overlayX: 'center', overlayY: 'top' },
      0,
      8, // offset Y
    ),
  ];

  onPositionChange(event: ConnectedOverlayPositionChange): void {
    const isTop = event.connectionPair.originY === 'top';
    this.placement.set(isTop ? 'top' : 'bottom');
  }

  show(): void {
    this.clearHideTimer();
    this.isOpen.set(true);
  }

  scheduleHide(): void {
    this.clearHideTimer();
    this.hideTimeoutId = setTimeout(() => {
      this.isOpen.set(false);
      this.hideTimeoutId = null;
    }, 180);
  }

  hide(): void {
    this.clearHideTimer();
    this.isOpen.set(false);
  }

  private parseHint(hint: string): HintBlock[] {
    const lines = hint
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line, index, source) => line !== '' || (index > 0 && source[index - 1] !== ''));

    const blocks: HintBlock[] = [];
    let paragraphLines: string[] = [];
    let listItems: string[] = [];

    const flushParagraph = (): void => {
      if (paragraphLines.length === 0) {
        return;
      }

      blocks.push({ kind: 'paragraph', segments: this.parseSegments(paragraphLines.join(' ')) });
      paragraphLines = [];
    };

    const flushList = (): void => {
      if (listItems.length === 0) {
        return;
      }

      blocks.push({ kind: 'list', items: listItems.map((item) => this.parseSegments(item)) });
      listItems = [];
    };

    for (const line of lines) {
      if (line === '') {
        flushParagraph();
        flushList();
        continue;
      }

      if (line.startsWith('- ') || line.startsWith('* ')) {
        flushParagraph();
        listItems.push(line.slice(2).trim());
        continue;
      }

      flushList();
      paragraphLines.push(line);
    }

    flushParagraph();
    flushList();

    return blocks;
  }

  private parseSegments(text: string): HintSegment[] {
    const segments: HintSegment[] = [];
    const urlPattern = /https?:\/\/[^\s]+/g;
    let lastIndex = 0;

    for (const match of text.matchAll(urlPattern)) {
      const matchIndex = match.index ?? 0;
      const rawUrl = match[0];
      const { url, trailingText } = this.extractUrlParts(rawUrl);

      this.appendTextSegment(segments, text.slice(lastIndex, matchIndex));
      segments.push({ kind: 'link', text: url, href: url });
      this.appendTextSegment(segments, trailingText);

      lastIndex = matchIndex + rawUrl.length;
    }

    this.appendTextSegment(segments, text.slice(lastIndex));

    return segments;
  }

  private extractUrlParts(rawUrl: string): { url: string; trailingText: string } {
    let urlEndIndex = rawUrl.length;

    while (urlEndIndex > 0 && '.,;!?)'.includes(rawUrl[urlEndIndex - 1])) {
      urlEndIndex -= 1;
    }

    return {
      url: rawUrl.slice(0, urlEndIndex),
      trailingText: rawUrl.slice(urlEndIndex),
    };
  }

  private appendTextSegment(segments: HintSegment[], text: string): void {
    if (text === '') {
      return;
    }

    const lastSegment = segments.at(-1);

    if (lastSegment?.kind === 'text') {
      lastSegment.text += text;
      return;
    }

    segments.push({ kind: 'text', text });
  }

  private clearHideTimer(): void {
    if (this.hideTimeoutId === null) {
      return;
    }

    clearTimeout(this.hideTimeoutId);
    this.hideTimeoutId = null;
  }
}
